import React from 'react'
import { GridSize, Typography } from 'transfix-ui/core'
import { IShipmentIssueStates } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import SLADueStatus from 'apps/marketplace/pages/issues/shared/SLADueStatus/SLADueStatus'
import { Avatar, Tag, TAG_SIZE } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { statusSelectList } from '../constants'
import { IShipmentIssueDetailsFragment } from '../graphql/ShipmentIssueDetailsFragment'

export type TColumnName =
  | 'issueId'
  | 'type'
  | 'category'
  | 'priority'
  | 'sla'
  | 'owner'
  | 'subscribers'
  | 'status'

export interface IColumnConfig {
  weight: GridSize | [GridSize, GridSize]
  order: number | [number, number]
  label: string
  visibleOnReduced: boolean
  getValue: (issue: IShipmentIssueDetailsFragment) => React.ReactNode
}

export interface IColumn extends IColumnConfig {
  key: string
  weight: GridSize
}

export const columnWeights: Record<TColumnName, IColumnConfig> = {
  issueId: {
    label: 'Issue ID',
    order: 1,
    weight: 1,
    visibleOnReduced: true,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        {issue.id}
      </Typography>
    ),
  },
  type: {
    label: 'Type',
    order: 2,
    weight: [4, 2],
    visibleOnReduced: true,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        {issue.type.name}
      </Typography>
    ),
  },
  category: {
    label: 'Category',
    order: 3,
    weight: 2,
    visibleOnReduced: false,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        {issue.type.category?.name}
      </Typography>
    ),
  },
  priority: {
    label: 'Priority',
    order: 5,
    weight: 1,
    visibleOnReduced: true,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        {issue.priority || DISPLAY.empty}
      </Typography>
    ),
  },
  sla: {
    label: 'SLA Due',
    order: 6,
    weight: 2,
    visibleOnReduced: true,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        <SLADueStatus priority={issue.priority} dueAt={issue.dueAt} issueStatus={issue.state} />
      </Typography>
    ),
  },
  owner: {
    label: 'Owner',
    order: 6,
    weight: 1,
    visibleOnReduced: true,
    getValue: issue => (
      <Typography variant='body2' component='span'>
        {issue.owner.firstName ? (
          <Avatar name={`${issue.owner.firstName} ${issue.owner.lastName}`} />
        ) : (
          DISPLAY.empty
        )}
      </Typography>
    ),
  },
  subscribers: {
    label: 'Subscribers',
    order: 7,
    weight: 2,
    visibleOnReduced: false,
    getValue: issue =>
      issue.subscribers.length ? (
        issue.subscribers.map(subscriber => (
          <Avatar
            key={subscriber.followerId}
            name={`${subscriber.firstName} ${subscriber.lastName}`}
          />
        ))
      ) : (
        <Typography variant='body2' component='span'>
          {DISPLAY.empty}
        </Typography>
      ),
  },
  status: {
    label: 'Status',
    order: 7,
    weight: [3, 1],
    visibleOnReduced: true,
    getValue: issue => {
      const currentState = statusSelectList.find(status => status.value === issue.state)

      return (
        <Tag
          size={TAG_SIZE.xsmall}
          label={currentState?.label || ''}
          tagStyle={currentState?.style}
        />
      )
    },
  },
}

export const useIssueRowColumns = (reducedVersion: boolean): IColumn[] => {
  const issueSLAsAndEscalationEnabled = useFeatureFlag('issueSLAsAndEscalation')
  return Object.entries(columnWeights)
    .filter(([key, { label, visibleOnReduced }]) => {
      if (!label) return false
      if (!issueSLAsAndEscalationEnabled && key === 'sla') return false
      return reducedVersion ? visibleOnReduced : true
    })
    .map(([key, { weight, ...value }]) => {
      if (Array.isArray(weight)) {
        const [compactWeight, normalWeight] = weight
        return {
          key,
          weight: reducedVersion ? compactWeight : normalWeight,
          ...value,
        }
      }

      return {
        key,
        weight,
        ...value,
      }
    })
}

export const STATE_TRANSITION_DESCRIPTION: Record<IShipmentIssueStates, string> = {
  [IShipmentIssueStates.awaiting_response]: 'Awaiting Response status by',
  [IShipmentIssueStates.in_progress]: 'In Progress status by',
  [IShipmentIssueStates.pending]: 'In Pending status by',
  [IShipmentIssueStates.resolved]: 'Resolved by',
}

export const stepsToResolveToResolutionSteps = (steps: string[]) =>
  (steps || []).map((step, index) => ({
    description: step,
    stepSequence: index + 1,
  }))
